import React, { useEffect, useRef, useState } from 'react';
import {
  Check, Copy, Loader2, ChevronRight, ChevronLeft, Wallet,
  AlertTriangle, X, CreditCard, Lock, RefreshCw, MessageCircle,
  Clock, ExternalLink,
} from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { Badge, Dot } from './Badge';
import { apiFetch } from '../api';
import { copyToClipboard, formatRelative, playSuccessChime, showNotification } from '../lib/utils';

/**
 * CheckoutDialog — onboarding-stepper flow for paying.
 *
 *  Step 1 — Choose payment method
 *    • Credit / debit card (Wayl — Iraqi fiat gateway, IQD)
 *    • Pay with crypto (NOWPayments — USDT/BTC/ETH/etc.)
 *
 *  Step 2 — Choose crypto currency (crypto flow only)
 *    • Grid of supported coins
 *    • Pre-flight /payments/min-amount check per coin
 *    • Disable coins whose minimum > amount
 *
 *  Step 3 — Pay
 *    • Wayl: hosted checkout URL — "Continue to Wayl" button
 *    • Crypto: big address display + live status polling every 5s
 *    • Auto-close on "finished"
 *    • Helpful error on failed/expired/partially_paid
 *
 * Props:
 *  - open:        bool  (visibility)
 *  - kind:             'subscription' | 'topup'
 *  - planCode:         string (for subscription)
 *  - usdCents:         number (for topup)
 *  - billingInterval:   'monthly' | 'quarterly' (for subscription)
 *  - expectedCents:     number (server-computed price for change — used to
 *                        tell the user the exact amount they'll be charged
 *                        for an upgrade, computed by the server-side
 *                        plan_changes module)
 *  - planName:    string (display)
 *  - onClose:     () => void
 *  - onSuccess:   () => void  (called when payment finishes successfully)
 */
export function CheckoutDialog({ open, kind, planCode, usdCents, planName, billingInterval = 'monthly', onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);          // 'crypto' | 'card'
  const [currency, setCurrency] = useState(null);      // {code, name, network, min_usd}
  const [minAmounts, setMinAmounts] = useState({});    // cache of currency → min_usd
  const [minLoading, setMinLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [intent, setIntent] = useState(null);          // {payment_id, pay_address, pay_amount, ...}
  const [status, setStatus] = useState('pending');     // live status from polling
  const [copied, setCopied] = useState(null);
  const pollRef = useRef(null);

  // Reset everything when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setMethod(null);
      setCurrency(null);
      setMinAmounts({});
      setMinLoading(false);
      setCreating(false);
      setCreateError(null);
      setIntent(null);
      setStatus('pending');
      setCopied(null);
    }
  }, [open]);

  // Stop polling when dialog closes
  useEffect(() => {
    if (!open && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [open]);

  // Wayl is hosted-checkout-only: no currency picker, no wallet polling.
  // When user picks "Credit / debit card" we go straight to Step 3 and
  // fire the checkout API immediately.
  useEffect(() => {
    if (method !== 'card' || step !== 3 || intent) return;
    let cancelled = false;
    async function go() {
      setCreating(true);
      setCreateError(null);
      try {
        const params = new URLSearchParams();
        if (kind === 'subscription') {
          params.set('plan_code', planCode);
          params.set('billing_interval', billingInterval);
        } else {
          params.set('usd_cents', String(usdCents));
        }
        // pay_currency is required by the API but ignored by Wayl.
        params.set('pay_currency', 'IQD');
        // Force the Wayl provider even if PAYMENT_PROVIDER env defaults
        // to nowpayments — the customer explicitly picked the card method.
        params.set('provider', 'wayl');
        const path = kind === 'subscription'
          ? `/payments/checkout/subscription?${params}`
          : `/payments/checkout/topup?${params}`;
        const intentResp = await apiFetch(path, { method: 'POST' });
        if (cancelled) return;
        setIntent(intentResp);
        if (intentResp.hosted_url) {
          // Open Wayl's hosted checkout in a new tab — customer completes
          // payment there, returns to our success_url, our webhook fires.
          // Append currency=usd so Wayl renders the USD price.
          const sep = intentResp.hosted_url.includes('?') ? '&' : '?';
          window.open(
            `${intentResp.hosted_url}${sep}currency=usd`,
            '_blank',
            'noopener,noreferrer'
          );
        }
      } catch (e) {
        if (!cancelled) {
          setCreateError(e.detail?.message || e.detail || e.message || 'Checkout failed');
        }
      } finally {
        if (!cancelled) setCreating(false);
      }
    }
    go();
    return () => { cancelled = true; };
  }, [method, step, intent, kind, planCode, billingInterval, usdCents]);

  // Fetch min-amounts for all supported currencies when we arrive at step 2
  useEffect(() => {
    if (step !== 2 || minLoading || Object.keys(minAmounts).length > 0) return;
    setMinLoading(true);
    // Hardcoded list — must match backend SUPPORTED_PAY_CURRENCIES
    const currencies = [
      'usdttrc20', 'usdterc20', 'btc', 'eth',
      'busdbsc', 'sol', 'ton', 'usdcsol',
    ];
    Promise.all(
      currencies.map((c) =>
        apiFetch(`/payments/min-amount?pay_currency=${c}&provider=nowpayments`)
          .then((r) => [c, r])
          .catch(() => [c, { min_amount: null, known: false }])
      )
    ).then((entries) => {
      const am = Object.fromEntries(entries);
      setMinAmounts(am);
      setMinLoading(false);
      // Auto-select the first currency that fits the amount (so the
      // user can just click Continue without having to pick anything).
      if (!currency) {
        const recommended = recommendCurrencies(usdCents, am, SUPPORTED_CURRENCIES);
        if (recommended.length > 0) {
          setCurrency(recommended[0]);
        }
      }
    });
  }, [step, minLoading, minAmounts, usdCents, currency]);

  // Poll the payment status every 5s after creating the intent.
  // Also exposes `refreshNow` for the "I sent it" button.
  const [refreshing, setRefreshing] = useState(false);
  const tickRef = useRef(null);  // latest tick fn for manual trigger
  const prevStatusRef = useRef(null);
  useEffect(() => {
    if (!intent) return;
    let cancelled = false;
    async function tick() {
      try {
        const r = await apiFetch(`/payments/${intent.payment_id}`);
        if (cancelled) return;
        const newStatus = r.status;
        const prevStatus = prevStatusRef.current;
        setStatus(newStatus);
        if (newStatus !== prevStatus) {
          // Status changed — fire feedback effects
          if (newStatus === 'finished' && prevStatus && prevStatus !== 'finished') {
            // First time we see finished — play chime + notify
            playSuccessChime();
            showNotification(
              'Payment received ✓',
              kind === 'subscription'
                ? `Your ${planName} subscription is now active.`
                : `${usdCents / 100} USD of credits have been added.`
            );
          }
          prevStatusRef.current = newStatus;
        }
        if (newStatus === 'finished') {
          // Stop polling, notify parent
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          // Small delay so the user sees the "finished" state before close
          setTimeout(() => onSuccess?.(), 1500);
        }
      } catch (e) {
        // Network blip — ignore, retry next tick
      }
    }
    tickRef.current = tick;
    tick();
    pollRef.current = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [intent, kind, planName, usdCents, onSuccess]);

  async function refreshNow() {
    if (!tickRef.current || refreshing) return;
    setRefreshing(true);
    try {
      await tickRef.current();
    } finally {
      // Brief delay so the user sees the spinner even on a fast response
      setTimeout(() => setRefreshing(false), 500);
    }
  }

  // Track whether we've shown the "still waiting" hint after the user
  // clicked "I sent it" and the status was still pending.
  const [recentCheck, setRecentCheck] = useState(null);
  // When refreshNow runs, capture that we just checked. The Pay dialog
  // shows a small note next to the status badge if the check came back
  // still waiting.
  async function refreshNowWithNote() {
    if (!tickRef.current || refreshing) return;
    setRefreshing(true);
    try {
      await tickRef.current();
    } finally {
      setTimeout(() => {
        // If status is still 'waiting', show a hint after the check
        setRecentCheck({
          at: Date.now(),
          stillWaiting: status === 'waiting',
        });
        setRefreshing(false);
      }, 500);
    }
  }

  if (!open) return null;

  // Helpers
  async function copy(field, value) {
    if (!value) return;
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(field);
      setTimeout(() => setCopied(null), 1500);
    }
  }

  // Min-amount disabled check: amount (in USD cents) must be >= min_amount (in USD-equivalent)
  function isCurrencyDisabled(code) {
    const m = minAmounts[code];
    if (!m || m.min_amount == null) return false;  // unknown → don't disable
    return usdCents < m.min_amount * 100;            // both in cents
  }

  async function createInvoice() {
    setCreating(true);
    setCreateError(null);
    try {
      const params = new URLSearchParams();
      if (kind === 'subscription') {
        params.set('plan_code', planCode);
        params.set('billing_interval', billingInterval);
      } else {
        params.set('usd_cents', String(usdCents));
      }
      params.set('pay_currency', currency.code);
      // Force the NOWPayments provider even if PAYMENT_PROVIDER env
      // defaults to wayl — the customer explicitly picked crypto.
      params.set('provider', 'nowpayments');
      const path = kind === 'subscription'
        ? `/payments/checkout/subscription?${params}`
        : `/payments/checkout/topup?${params}`;
      const intentResp = await apiFetch(path, { method: 'POST' });
      setIntent(intentResp);
      setStep(3);
    } catch (e) {
      setCreateError(e.detail || e.message || 'Checkout failed');
    } finally {
      setCreating(false);
    }
  }

  function retryCreate() {
    setIntent(null);
    setCreateError(null);
    setStep(2);
  }

  // ----- Steps -----

  const stepper = (
    <div className="flex items-center justify-center gap-2 mb-5">
      {[1, 2, 3].map((n) => (
        <React.Fragment key={n}>
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
              step === n
                ? 'bg-brand text-brand-fg'
                : step > n
                ? 'bg-brand/20 text-brand'
                : 'bg-surface-2 text-fg-muted'
            }`}
          >
            {step > n ? <Check className="h-3.5 w-3.5" /> : n}
          </div>
          {n < 3 && (
            <div className={`w-8 h-px ${step > n ? 'bg-brand/40' : 'bg-border'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const STEP_TITLES = {
    1: 'How would you like to pay?',
    2: 'Choose your cryptocurrency',
    3: 'Send the payment',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={STEP_TITLES[step]}
      description={
        kind === 'subscription'
          ? `Upgrading to ${planName}${billingInterval === 'quarterly' ? ' (quarterly)' : ''}`
          : `Top up ${(usdCents / 100).toFixed(2)} USD of credits`
      }
      size={step === 3 ? 'lg' : 'md'}
    >
      {stepper}

      {/* ===== STEP 1: Payment method ===== */}
      {step === 1 && (
        <div className="space-y-2.5">
          <PaymentMethodButton
            icon={<CreditCard className="h-5 w-5" />}
            title="Credit / debit card"
            subtitle="cards, Visa, Mastercard — via Wayl"
            onClick={() => {
              setMethod('card');
              setStep(3);  // skip Step 2 (no currency picker for Wayl)
            }}
          />
          <PaymentMethodButton
            icon={<Wallet className="h-5 w-5" />}
            title="Pay with crypto"
            subtitle="USDT, Bitcoin, Ethereum, Solana, and more — via NOWPayments"
            recommended
            onClick={() => {
              setMethod('crypto');
              setStep(2);
            }}
          />
        </div>
      )}

      {/* ===== STEP 2: Currency picker ===== */}
      {step === 2 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back
            </button>
            {minLoading && (
              <div className="flex items-center gap-1.5 text-2xs text-fg-muted">
                <Loader2 className="h-3 w-3 animate-spin" />
                Checking minimums…
              </div>
            )}
          </div>

          {/* Recommendation hint */}
          {!minLoading && (() => {
            const recommended = recommendCurrencies(usdCents, minAmounts, SUPPORTED_CURRENCIES);
            const usdtFit = SUPPORTED_CURRENCIES.filter(
              c => c.family === 'USDT' && minAmounts[c.code]?.min_amount != null && usdCents >= minAmounts[c.code].min_amount * 100
            );
            const anyFit = recommended.length > 0;
            if (!anyFit) {
              return (
                <div className="mb-3 p-3 rounded-md bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300">
                  <strong>Amount too small for any currency.</strong>
                  <p className="text-xs mt-1 text-rose-300/80">
                    The smallest crypto minimum is above ${(usdCents/100).toFixed(2)}.
                    Please increase the amount to at least $5 to continue.
                  </p>
                </div>
              );
            }
            if (usdtFit.length === 0) {
              // USDT variants are too high — recommend USDC or alt L1s
              return (
                <div className="mb-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20 text-sm text-amber-200">
                  <strong>USDT network minimum is above ${(usdCents/100).toFixed(2)}.</strong>
                  <p className="text-xs mt-1 text-amber-300/80">
                    Pick one of the highlighted currencies below — they have a lower minimum for this amount.
                  </p>
                </div>
              );
            }
            return null;
          })()}

          <div className="grid grid-cols-2 gap-2">
            {(() => {
              // Reorder: fit-amount currencies first (USDT > USDC > others), then blocked ones
              const fits = (c) => {
                const m = minAmounts[c.code];
                if (!m || m.min_amount == null) return true;
                return usdCents >= m.min_amount * 100;
              };
              const recommended = recommendCurrencies(usdCents, minAmounts, SUPPORTED_CURRENCIES);
              const blocked = SUPPORTED_CURRENCIES.filter(c => !fits(c));
              const ordered = [...recommended, ...blocked];
              return ordered.map((c) => {
                const minUsd = minAmounts[c.code]?.min_amount ?? null;
                const disabled = minUsd !== null && usdCents < minUsd * 100;
                const selected = currency?.code === c.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    disabled={disabled}
                    onClick={() => setCurrency(c)}
                    className={`relative text-left p-3 rounded-md border transition-all ${
                      selected
                        ? 'border-brand bg-brand/10'
                        : disabled
                        ? 'border-border bg-surface-2/40 opacity-50 cursor-not-allowed'
                        : 'border-border bg-surface hover:bg-surface-2 hover:border-border-strong'
                    }`}
                  >
                    {selected && (
                      <Check className="absolute top-2 right-2 h-3.5 w-3.5 text-brand" />
                    )}
                    <div className="font-medium text-sm text-fg">{c.name}</div>
                    <div className="text-2xs text-fg-muted mt-0.5">{c.network}</div>
                    <div className="text-2xs text-fg-subtle mt-1.5">
                      {minUsd === null && minLoading ? (
                        '…'
                      ) : minUsd !== null ? (
                        disabled ? (
                          <span className="text-rose-400">
                            Min ~${minUsd.toFixed(2)} — too low
                          </span>
                        ) : (
                          <span className="text-fg-subtle">Min ~${minUsd.toFixed(2)}</span>
                        )
                      ) : null}
                    </div>
                  </button>
                );
              });
            })()}
          </div>

          {createError && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>{typeof createError === 'string' ? createError : (createError.message || createError.code || 'Checkout failed')}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-border">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={createInvoice}
              disabled={!currency || creating || minLoading}
            >
              {creating ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating…</>
              ) : (
                <>Continue <ChevronRight className="h-3.5 w-3.5" /></>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ===== STEP 3: Pay + live status =====
         Don't gate the whole block on `intent` — WaylPayDisplay shows its
         own loader when intent is null, and the user needs immediate
         feedback that something is happening. */}
      {step === 3 && method === 'card' && (
        <WaylPayDisplay
          intent={intent}
          creating={creating}
          createError={createError}
          onRetry={retryCreate}
          onClose={onClose}
          onCheckStatus={async () => {
            if (!intent) return;
            try {
              const r = await apiFetch(`/payments/${intent.payment_id}`);
              setStatus(r.status);
              if (r.status === 'finished') {
                playSuccessChime();
                showNotification(
                  'Payment received ✓',
                  kind === 'subscription'
                    ? `Your ${planName} subscription is now active.`
                    : `${usdCents / 100} USD of credits have been added.`
                );
                setTimeout(() => onSuccess?.(), 1200);
              }
            } catch (e) {
              // ignore
            }
          }}
        />
      )}
      {step === 3 && method === 'crypto' && intent && (
        <>
              <PayAddressDisplay
                intent={intent}
                status={status}
                copied={copied}
                onCopy={copy}
                recentCheck={recentCheck}
              />
              <StatusHelp status={status} onRetry={retryCreate} />

              <div className="flex justify-between items-center gap-2 pt-2">
                <Button
                  variant="secondary"
                  onClick={refreshNowWithNote}
                  disabled={refreshing || status === 'finished'}
                >
                  {refreshing ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</>
                  ) : (
                    <><Check className="h-3.5 w-3.5" /> I sent it</>
                  )}
                </Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={onClose}>Close</Button>
              {status !== 'finished' && status !== 'pending' && (
                <Button variant="secondary" onClick={retryCreate}>
                  <RefreshCw className="h-3.5 w-3.5" />
                  Try again
                </Button>
              )}
                </div>
              </div>
            </>
          )}
    </Dialog>
  );
}

// --- Sub-components ---

function PaymentMethodButton({ icon, title, subtitle, recommended, disabled, badge, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`relative w-full flex items-start gap-3 p-4 rounded-md border text-left transition-colors ${
        disabled
          ? 'border-border bg-surface-2/40 opacity-60 cursor-not-allowed'
          : 'border-border bg-surface hover:bg-surface-2 hover:border-border-strong'
      }`}
    >
      <div className="h-10 w-10 rounded-md bg-surface-2 flex items-center justify-center text-brand shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-fg">{title}</span>
          {recommended && <Badge variant="brand">Recommended</Badge>}
          {badge && <Badge variant="default">{badge}</Badge>}
        </div>
        <div className="text-xs text-fg-muted mt-0.5">{subtitle}</div>
      </div>
    </button>
  );
}

/**
 * Wayl hosted-checkout view (Step 3 for card flow).
 * Shows the IQD amount + a big "Continue to Wayl" button that opens
 * the hosted checkout in a new tab. Customer completes payment there,
 * returns to our success_url, our webhook fires, status flips to
 * 'finished'.
 */
function WaylPayDisplay({ intent, creating, createError, onRetry, onClose, onCheckStatus }) {
  // Show a loader any time we don't have an intent yet — whether we're
  // still in flight, just landed on the step, or recovering after close.
  if (!intent && !createError) {
    return (
      <div className="p-8 text-center space-y-3">
        <Loader2 className="h-10 w-10 mx-auto animate-spin text-brand" />
        <p className="text-sm font-medium text-fg">Preparing your secure checkout…</p>
        <p className="text-xs text-fg-muted">Just a moment.</p>
      </div>
    );
  }
  if (createError) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Card checkout unavailable.</strong>
            <p className="text-xs text-rose-300/80 mt-1">
              {typeof createError === 'string' ? createError : (createError?.message || createError?.code || 'Checkout failed')}
            </p>
            <p className="text-xs text-rose-300/80 mt-1">
              Pick "Pay with crypto" instead, or contact support.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="secondary" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" />
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (!intent) {
    return null;
  }
  return (
    <div className="space-y-5">
      {/* Cozy header: emoji + welcome message */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 text-2xl">
          💳
        </div>
        <div className="text-sm text-fg-muted">
          You're being redirected to our secure payment partner
        </div>
      </div>

      {/* Big amount card */}
      <div className="rounded-lg bg-gradient-to-br from-surface-2 to-surface border border-border-strong p-6 text-center shadow-sm">
        <div className="text-2xs font-semibold text-fg-muted uppercase tracking-wider mb-2">
          You'll pay
        </div>
        <div className="flex items-baseline justify-center gap-2 mb-1">
          <span className="text-5xl font-bold text-fg tabular-nums tracking-tight">
            ${((intent.pay_amount || 0) / 1420).toFixed(2)}
          </span>
          <span className="text-xl text-fg-muted uppercase font-medium">USD</span>
        </div>
        <div className="text-sm text-fg-muted">
          ≈ {Math.round(intent.pay_amount || 0).toLocaleString()} IQD
        </div>
      </div>

      {/* Big "Go to checkout" button — the call to action.
          Append &currency=usd so Wayl renders prices in USD. */}
      {intent.hosted_url ? (
        <a
          href={
            intent.hosted_url.includes('?')
              ? `${intent.hosted_url}&currency=usd`
              : `${intent.hosted_url}?currency=usd`
          }
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full"
        >
          <Button variant="primary" size="lg" className="w-full text-base font-semibold">
            <ExternalLink className="h-5 w-5" />
            Go to secure checkout
          </Button>
        </a>
      ) : (
        <div className="text-center text-sm text-rose-300 p-3 rounded-md bg-rose-500/10 border border-rose-500/20">
          Checkout URL unavailable. Please contact support.
        </div>
      )}

      {/* Trust row */}
      <div className="flex items-center justify-center gap-5 text-xs text-fg-muted">
        <span className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5" />
          SSL encrypted
        </span>
        <span className="flex items-center gap-1.5">
          <CreditCard className="h-3.5 w-3.5" />
          Visa · Mastercard
        </span>
        <span className="flex items-center gap-1.5">
          <Check className="h-3.5 w-3.5" />
          Secure
        </span>
      </div>

      {/* How it works */}
      <div className="text-xs text-fg-muted leading-relaxed p-3.5 rounded-md bg-surface-2/60 border border-border space-y-1.5">
        <p className="font-medium text-fg">What happens next:</p>
        <p>1. Click the green button above — a new tab opens to Wayl's secure checkout.</p>
        <p>2. Pay with cards, Visa, Master.</p>
        <p>3. Once paid, you'll be redirected back here and your credits unlock automatically.</p>
      </div>

      {/* Secondary actions */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCheckStatus}>
          <Check className="h-3.5 w-3.5" /> I already paid — check status
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
      </div>

      {/* Payment ID for support */}
      <details className="text-xs">
        <summary className="cursor-pointer text-fg-muted hover:text-fg select-none">Show payment ID</summary>
        <div className="mt-2 mono-key break-all text-2xs text-fg-muted">{intent.payment_id}</div>
      </details>
    </div>
  );
}

function PayAddressDisplay({ intent, status, copied, onCopy, recentCheck }) {
  return (
    <>
      <StatusBadge status={status} recentCheck={recentCheck} />

      {/* Pay amount + currency */}
      <div className="rounded-md bg-surface-2 border border-border p-4 text-center">
        <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider mb-1">
          Send exactly
        </div>
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-3xl font-semibold text-fg tabular-nums">
            {intent.pay_amount || '—'}
          </span>
          <span className="text-base text-fg-muted uppercase font-medium">
            {intent.pay_currency || ''}
          </span>
        </div>
      </div>

      {/* Pay address */}
      <div>
        <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider mb-1.5">
          To this address
        </div>
        <div className="flex gap-2">
          <code className="mono-key flex-1 break-all">
            {intent.pay_address}
          </code>
          <Button
            variant="secondary"
            size="md"
            onClick={() => onCopy('address', intent.pay_address)}
            className="shrink-0"
          >
            {copied === 'address' ? (
              <><Check className="h-3.5 w-3.5 text-success" /> Copied</>
            ) : (
              <><Copy className="h-3.5 w-3.5" /> Copy</>
            )}
          </Button>
        </div>
      </div>

      {/* Payment ID for support */}
      <details className="text-xs">
        <summary className="cursor-pointer text-fg-muted hover:text-fg">Show payment ID</summary>
        <div className="mt-2 flex gap-2">
          <code className="mono-key flex-1 break-all text-2xs">
            {intent.payment_id}
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCopy('id', intent.payment_id)}
            className="shrink-0"
          >
            {copied === 'id' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </Button>
        </div>
      </details>
    </>
  );
}

function StatusBadge({ status, recentCheck }) {
  const config = {
    waiting:        { variant: 'warning',    label: 'Awaiting payment',     spin: false, Icon: Loader2 },
    confirming:     { variant: 'warning',    label: 'Confirming on blockchain', spin: true,  Icon: Loader2 },
    confirmed:      { variant: 'warning',    label: 'Confirmed, processing',     spin: true,  Icon: Loader2 },
    wrong_asset:    { variant: 'destructive',label: 'Wrong coin sent',          spin: false, Icon: AlertTriangle },
    sending:        { variant: 'warning',    label: 'Sending to your wallet',    spin: true,  Icon: Loader2 },
    partially_paid: { variant: 'warning',    label: 'Partially paid',            spin: false, Icon: AlertTriangle },
    pending:        { variant: 'warning',    label: 'Awaiting payment',          spin: false, Icon: Loader2 },
    finished:       { variant: 'success',    label: 'Payment confirmed',         spin: false, Icon: Check },
    failed:         { variant: 'destructive',label: 'Payment failed',            spin: false, Icon: AlertTriangle },
    expired:        { variant: 'destructive',label: 'Payment expired',           spin: false, Icon: AlertTriangle },
    refunded:       { variant: 'destructive',label: 'Payment refunded',          spin: false, Icon: AlertTriangle },
    cancelled:      { variant: 'destructive',label: 'Payment cancelled',         spin: false, Icon: AlertTriangle },
  };
  const c = config[status] || config.pending;
  const stillWaitingNote = recentCheck?.stillWaiting && status === 'waiting'
    ? `Checked just now — we haven't received your payment yet. Confirmation usually takes 5-30 minutes.`
    : null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-3 rounded-md bg-surface-2 border border-border">
        <Badge variant={c.variant}>
          {c.spin ? <c.Icon className="h-3 w-3 animate-spin" /> : <c.Icon className="h-3 w-3" />}
          {c.label}
        </Badge>
        <span className="text-xs text-fg-muted ml-auto">
          {status === 'finished' ? 'Updating your account…' : 'Auto-updates every 5 seconds'}
        </span>
      </div>
      {stillWaitingNote && (
        <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
          <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{stillWaitingNote}</span>
        </div>
      )}
    </div>
  );
}

function StatusHelp({ status, onRetry }) {
  if (status === 'partially_paid') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/20 text-sm text-amber-200">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>You sent less than required.</strong>
          <p className="text-xs text-amber-300/80 mt-1">
            NOWPayments detected your payment but the amount was lower than the expected total.
            The remaining difference was not received. Please contact support at{' '}
            <a href="mailto:support@datames.com" className="underline">support@datames.com</a>{' '}
            with your payment ID to resolve this manually.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>Payment failed.</strong>
          <p className="text-xs text-rose-300/80 mt-1">
            This usually means the amount sent was below the network's minimum.
            Click "Try again" to pick a different coin or top up a larger amount.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'expired') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>Payment expired.</strong>
          <p className="text-xs text-rose-300/80 mt-1">
            No deposit was detected within 7 days. Click "Try again" to create a fresh invoice.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'wrong_asset_confirmed' || status === 'wrong_asset') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>Wrong network or coin sent.</strong>
          <p className="text-xs text-rose-300/80 mt-1">
            You sent crypto using the wrong network or coin. Contact{' '}
            <a href="mailto:support@datames.com" className="underline">support@datames.com</a>{' '}
            for manual recovery.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'refunded' || status === 'cancelled') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-surface-2 border border-border text-sm text-fg-muted">
        <MessageCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>This payment was refunded.</strong>
          <p className="text-xs mt-1">
            No credits were granted. Click "Try again" to start over.
          </p>
        </div>
      </div>
    );
  }
  if (status === 'finished') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-md bg-success-soft border border-success/20 text-sm text-success-fg">
        <Check className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>Payment received.</strong>
          <p className="text-xs text-success-fg/80 mt-1">
            Credits are being added to your account. This dialog will close automatically.
          </p>
        </div>
      </div>
    );
  }
  // Default: waiting/confirming/confirmed/sending — show instructions
  return (
    <div className="text-xs text-fg-muted leading-relaxed p-3 rounded-md bg-surface-2/40 border border-border">
      Send the exact amount shown above to that address. Your account will be credited
      automatically as soon as the payment confirms on the blockchain — usually 5-30 minutes.
    </div>
  );
}

// --- Constants ---

// Order matters: USDT variants first (most familiar), then USDC, then
// major L1 coins, then alt L1s. The UI re-orders dynamically based on
// whether the amount fits the minimum, so this is the fallback order.
const SUPPORTED_CURRENCIES = [
  // USDT — different chains, prioritized for familiarity
  { code: 'usdttrc20', name: 'USDT (TRC20)',  network: 'Tron',       family: 'USDT' },
  { code: 'usdterc20', name: 'USDT (ERC20)',  network: 'Ethereum',   family: 'USDT' },
  { code: 'usdtbsc',   name: 'USDT (BSC)',    network: 'BNB Chain',  family: 'USDT' },
  { code: 'usdtpolygon', name: 'USDT (Polygon)', network: 'Polygon',  family: 'USDT' },
  { code: 'usdtsol',   name: 'USDT (Solana)', network: 'Solana',     family: 'USDT' },
  // USDC — different chains
  { code: 'usdcerc20', name: 'USDC (ERC20)',  network: 'Ethereum',   family: 'USDC' },
  { code: 'usdcsol',   name: 'USDC (Solana)', network: 'Solana',     family: 'USDC' },
  { code: 'usdcbsc',   name: 'USDC (BSC)',    network: 'BNB Chain',  family: 'USDC' },
  // BUSD
  { code: 'busdbsc',   name: 'BUSD (BSC)',    network: 'BNB Chain',  family: 'BUSD' },
  // Native L1 coins — only useful for larger amounts
  { code: 'btc',       name: 'Bitcoin',       network: 'Bitcoin',    family: 'BTC' },
  { code: 'eth',       name: 'Ethereum',      network: 'Ethereum',   family: 'ETH' },
  { code: 'sol',       name: 'Solana',        network: 'Solana',     family: 'SOL' },
  { code: 'ton',       name: 'TON',           network: 'TON',        family: 'TON' },
];

// Order to RECOMMEND when the customer's amount is too low for USDT-TRC20.
// We always try USDT on the next-cheapest chain first.
const USDT_FALLBACK_CHAIN = ['usdttrc20', 'usdterc20', 'usdtbsc', 'usdtsol', 'usdtpolygon'];

/**
 * Pick the best currencies to highlight given the amount the customer
 * is paying. Prioritizes USDT variants, then USDC, then alt L1s.
 */
function recommendCurrencies(usdCents, minAmounts, all) {
  const fits = (code) => {
    const m = minAmounts[code];
    if (!m || m.min_amount == null) return true;
    return usdCents >= m.min_amount * 100;
  };
  // Prefer USDT family first
  const usdt = all.filter(c => c.family === 'USDT' && fits(c.code));
  const usdc = all.filter(c => c.family === 'USDC' && fits(c.code));
  const others = all.filter(c => !['USDT', 'USDC'].includes(c.family) && fits(c.code));
  return [...usdt, ...usdc, ...others];
}
